import React from 'react';
import './PrivacyPage.scss';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="container">
        <div className="page-header">
          <h1>Privacy Policy</h1>
          <p>Last updated: December 2024</p>
        </div>

        <div className="content">
          <section className="content-section">
            <h2>Information We Collect</h2>
            <h3>Personal Information</h3>
            <p>
              When you create an account, we collect your name, email address,
              and password. We may also collect optional profile information
              such as your learning preferences, educational background, and
              professional interests.
            </p>

            <h3>Learning Data</h3>
            <p>
              We track your course progress, quiz results, lesson completion
              rates, and learning patterns to personalize your educational
              experience and improve our AI recommendations.
            </p>

            <h3>AI-Generated Content</h3>
            <p>
              When you use our AI course generation features, we collect your
              prompts, preferences, and the courses you create to improve our AI
              models and service quality.
            </p>

            <h3>Usage Information</h3>
            <p>
              We automatically collect information about how you interact with
              our platform, including pages visited, time spent on lessons,
              device information, and IP address.
            </p>
          </section>

          <section className="content-section">
            <h2>How We Use Your Information</h2>
            <p>
              <strong>Educational Services:</strong> We use your data to provide
              personalized learning experiences, generate custom courses, track
              your progress, and recommend relevant content.
            </p>

            <p>
              <strong>AI Improvement:</strong> Your usage patterns and feedback
              help us train and improve our AI models to create better
              educational content for all users.
            </p>

            <p>
              <strong>Communication:</strong> We send you course updates,
              progress notifications, and important service announcements. You
              can opt out of non-essential communications.
            </p>

            <p>
              <strong>Analytics:</strong> We analyze usage data to understand
              how our platform is used and to identify areas for improvement.
            </p>
          </section>

          <section className="content-section">
            <h2>Information Sharing and Disclosure</h2>
            <p>
              <strong>We do not sell your personal information.</strong> We may
              share your information only in these limited circumstances:
            </p>

            <p>
              <strong>Service Providers:</strong> We work with trusted
              third-party services for hosting, analytics, payment processing,
              and email delivery. These providers are contractually required to
              protect your data.
            </p>

            <p>
              <strong>AI Training:</strong> Your course generation requests and
              learning patterns may be used in anonymized, aggregated form to
              improve our AI models.
            </p>

            <p>
              <strong>Legal Requirements:</strong> We may disclose information
              if required by law, court order, or to protect the rights and
              safety of our users.
            </p>
          </section>

          <section className="content-section">
            <h2>Data Security and Retention</h2>
            <p>
              We implement industry-standard security measures including
              encryption, secure servers, and regular security audits. Your
              payment information is processed by certified payment providers
              and never stored on our servers.
            </p>

            <p>
              We retain your account information as long as your account is
              active. Learning progress data is kept to maintain your
              educational continuity. You can request account deletion at any
              time.
            </p>
          </section>

          <section className="content-section">
            <h2>Cookies and Tracking</h2>
            <p>
              We use essential cookies for authentication and security. Optional
              analytics cookies help us understand usage patterns. You can
              control cookie preferences through your browser settings.
            </p>
          </section>

          <section className="content-section">
            <h2>International Users and GDPR</h2>
            <p>
              If you're located in the EU, you have additional rights under GDPR
              including data portability, the right to be forgotten, and the
              right to restrict processing. We process your data lawfully based
              on your consent and our legitimate business interests.
            </p>
          </section>

          <section className="content-section">
            <h2>Your Privacy Rights</h2>
            <p>
              <strong>Access:</strong> Request a copy of your personal data
            </p>
            <p>
              <strong>Correction:</strong> Update or correct your information
            </p>
            <p>
              <strong>Deletion:</strong> Request account and data deletion
            </p>
            <p>
              <strong>Export:</strong> Download your learning progress and
              generated courses
            </p>
            <p>
              <strong>Opt-out:</strong> Unsubscribe from marketing
              communications
            </p>
          </section>

          <section className="content-section">
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We'll notify you
              of significant changes via email or platform notification.
              Continued use of our service after changes indicates acceptance.
            </p>
          </section>

          <section className="content-section">
            <h2>Contact Us</h2>
            <p>
              For privacy-related questions, data requests, or concerns, contact
              us at:
            </p>
            <p>
              <strong>Email:</strong> privacy@aiuniversity.com
            </p>
            <p>
              <strong>General inquiries:</strong> hello@aiuniversity.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
